import React, { useState, useEffect, useRef } from 'react';
import { Star, Loader2, ChevronDown, Check, X } from 'lucide-react';
import { BACKEND_URL } from '@/config/api';

const API = `${BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('accessToken');

export default function BookRating({ manuscriptId, userId }) {
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hovered, setHovered] = useState(0);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [pendingRating, setPendingRating] = useState(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [error, setError] = useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (!manuscriptId || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/ratings/eligibility/${manuscriptId}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!res.ok) throw new Error('Failed to load rating status');
        const data = await res.json();
        if (!cancelled) setEligibility(data);
      } catch {
        if (!cancelled) setEligibility({ visible: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [manuscriptId, userId]);

  // Close modal on outside click
  useEffect(() => {
    if (!showChangeModal) return;
    const handler = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        cancelChange();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showChangeModal]);

  const cancelChange = () => {
    setShowChangeModal(false);
    setPendingRating(null);
    setSelectedReason('');
    setHovered(0);
  };

  const handleStarClick = async (value) => {
    if (!eligibility?.enabled || submitting) return;

    // If already rated and clicking a different value -> open change modal
    if (eligibility.current_rating !== null && value !== eligibility.current_rating) {
      setPendingRating(value);
      setShowChangeModal(true);
      return;
    }

    // If clicking same rating, ignore
    if (eligibility.current_rating === value) return;

    // New rating
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/ratings`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ manuscript_id: manuscriptId, rating: value }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit rating');
      }
      const data = await res.json();
      setEligibility((prev) => ({
        ...prev,
        current_rating: data.rating,
        rating_id: data.rating_id,
      }));
    } catch (e) {
      setError(e.message);
      setTimeout(() => setError(null), 4000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmChange = async () => {
    if (!selectedReason || !pendingRating || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/ratings/${eligibility.rating_id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rating: pendingRating, reason_code: selectedReason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update rating');
      }
      const data = await res.json();
      setEligibility((prev) => ({
        ...prev,
        current_rating: data.rating,
      }));
      cancelChange();
    } catch (e) {
      setError(e.message);
      setTimeout(() => setError(null), 4000);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !eligibility?.visible) return null;

  const currentRating = eligibility.current_rating || 0;
  const displayRating = showChangeModal && pendingRating ? pendingRating : (hovered || currentRating);

  return (
    <div className="relative w-full" data-testid="book-rating-container">
      {/* Stars row */}
      <div className="flex items-center justify-center gap-1 py-1.5">
        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mr-1.5">
          {currentRating > 0 ? 'Your Rating' : 'Rate This Book'}
        </span>
        {[1, 2, 3, 4, 5].map((value) => {
          const filled = value <= displayRating;
          const isDisabled = !eligibility.enabled;

          return (
            <button
              key={value}
              type="button"
              data-testid={`rating-star-${value}`}
              disabled={isDisabled || submitting}
              onClick={() => handleStarClick(value)}
              onMouseEnter={() => !isDisabled && !showChangeModal && setHovered(value)}
              onMouseLeave={() => !showChangeModal && setHovered(0)}
              className={`p-0.5 transition-all duration-150 ${
                isDisabled
                  ? 'cursor-not-allowed opacity-40'
                  : 'cursor-pointer hover:scale-110'
              }`}
              title={isDisabled ? 'Listen to more of this book to leave a rating' : `Rate ${value} star${value > 1 ? 's' : ''}`}
            >
              <Star
                className={`w-5 h-5 transition-colors duration-150 ${
                  filled
                    ? 'text-amber-400 fill-amber-400'
                    : 'text-slate-600 fill-transparent'
                }`}
              />
            </button>
          );
        })}
        {submitting && <Loader2 className="w-4 h-4 animate-spin text-amber-400 ml-1" />}
      </div>

      {/* Error toast */}
      {error && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-3 py-1 bg-red-900/90 text-red-200 text-[10px] rounded-lg whitespace-nowrap z-50 border border-red-700/50" data-testid="rating-error">
          {error}
        </div>
      )}

      {/* Change reason modal */}
      {showChangeModal && (
        <div
          ref={modalRef}
          className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 bg-slate-800 rounded-xl border border-slate-600/60 shadow-2xl p-3 z-50"
          data-testid="rating-change-modal"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-white">Update your rating</p>
            <button onClick={cancelChange} className="p-0.5 text-slate-400 hover:text-white" data-testid="rating-change-cancel-x">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-[10px] text-slate-400 mb-2">Tell us why you're changing your rating:</p>

          {/* Reason dropdown */}
          <select
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
            data-testid="rating-change-reason-select"
            className="w-full px-2 py-1.5 rounded-lg bg-slate-900 border border-slate-600 text-xs text-slate-200 outline-none focus:border-amber-500 mb-2 appearance-none"
          >
            <option value="">Select a reason...</option>
            {(eligibility.change_reasons || []).map((r) => (
              <option key={r.code} value={r.code}>{r.label}</option>
            ))}
          </select>

          <div className="flex gap-2">
            <button
              onClick={cancelChange}
              data-testid="rating-change-cancel-btn"
              className="flex-1 px-2 py-1 text-[10px] font-medium text-slate-300 bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmChange}
              disabled={!selectedReason || submitting}
              data-testid="rating-change-confirm-btn"
              className="flex-1 px-2 py-1 text-[10px] font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1"
            >
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
